import { useState, useEffect } from "react";
import { API } from "../App";
import Layout from "../components/Layout";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import { 
  Settings, 
  Palette, 
  Type, 
  Image, 
  Mail,
  Phone,
  Globe,
  Save,
  Upload,
  Loader2
} from "lucide-react";

const AdminSettings = () => {
  const [settings, setSettings] = useState({
    site_name: "LearnHub",
    site_logo: null,
    primary_color: "#6d28d9",
    secondary_color: "#059669",
    hero_title: "Your Learning Journey Starts Here",
    hero_subtitle: "Access lecture recordings, learning materials, and course content.",
    hero_image: null,
    footer_text: "© 2024 LearnHub. All rights reserved.",
    social_links: {},
    contact_email: "",
    contact_phone: ""
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setSettings(prev => ({ ...prev, ...response.data }));
    } catch (error) {
      console.error("Failed to load settings:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/settings`, settings, { withCredentials: true });
      toast.success("Settings saved successfully!");
    } catch (error) {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingLogo(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/admin/settings/logo`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setSettings(prev => ({ ...prev, site_logo: response.data.logo_url }));
      toast.success("Logo uploaded!");
    } catch (error) {
      toast.error("Failed to upload logo");
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleSocialLinkChange = (platform, url) => {
    setSettings(prev => ({
      ...prev,
      social_links: { ...prev.social_links, [platform]: url }
    }));
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-700"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8 animate-fade-in" data-testid="admin-settings">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-stone-900" style={{ fontFamily: 'Outfit' }}>
              Site Settings
            </h1>
            <p className="text-stone-600 mt-1">Customize your learning portal</p>
          </div>
          <Button 
            onClick={handleSave}
            disabled={saving}
            className="bg-violet-700 hover:bg-violet-800 rounded-full"
            data-testid="save-settings-btn"
          >
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Save Changes
              </>
            )}
          </Button>
        </div>

        <Tabs defaultValue="branding" className="w-full">
          <TabsList className="mb-6">
            <TabsTrigger value="branding">Branding</TabsTrigger>
            <TabsTrigger value="homepage">Homepage</TabsTrigger>
            <TabsTrigger value="contact">Contact</TabsTrigger>
            <TabsTrigger value="social">Social Links</TabsTrigger>
          </TabsList>

          {/* Branding Tab */}
          <TabsContent value="branding">
            <div className="grid md:grid-cols-2 gap-6">
              <Card className="bg-white rounded-xl border border-stone-100">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
                    <Type className="h-5 w-5 text-violet-700" />
                    Site Name
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Site Name</Label>
                    <Input
                      value={settings.site_name}
                      onChange={(e) => setSettings({ ...settings, site_name: e.target.value })}
                      placeholder="LearnHub"
                      className="mt-1"
                      data-testid="site-name-input"
                    />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white rounded-xl border border-stone-100">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
                    <Image className="h-5 w-5 text-violet-700" />
                    Logo
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center gap-4">
                    {settings.site_logo ? (
                      <img 
                        src={`${API.replace('/api', '')}${settings.site_logo}`} 
                        alt="Logo" 
                        className="w-16 h-16 object-contain rounded-lg border"
                      />
                    ) : (
                      <div className="w-16 h-16 bg-stone-100 rounded-lg flex items-center justify-center">
                        <Image className="h-8 w-8 text-stone-400" />
                      </div>
                    )}
                    <div>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleLogoUpload}
                        className="hidden"
                        id="logo-upload"
                      />
                      <label htmlFor="logo-upload">
                        <Button 
                          variant="outline" 
                          className="cursor-pointer"
                          disabled={uploadingLogo}
                          asChild
                        >
                          <span>
                            {uploadingLogo ? (
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            ) : (
                              <Upload className="h-4 w-4 mr-2" />
                            )}
                            Upload Logo
                          </span>
                        </Button>
                      </label>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white rounded-xl border border-stone-100 md:col-span-2">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
                    <Palette className="h-5 w-5 text-violet-700" />
                    Colors
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <Label>Primary Color</Label>
                      <div className="flex items-center gap-3 mt-1">
                        <input
                          type="color"
                          value={settings.primary_color}
                          onChange={(e) => setSettings({ ...settings, primary_color: e.target.value })}
                          className="w-12 h-12 rounded-lg cursor-pointer border-0"
                        />
                        <Input
                          value={settings.primary_color}
                          onChange={(e) => setSettings({ ...settings, primary_color: e.target.value })}
                          placeholder="#6d28d9"
                          className="flex-1"
                        />
                      </div>
                    </div>
                    <div>
                      <Label>Secondary Color</Label>
                      <div className="flex items-center gap-3 mt-1">
                        <input
                          type="color"
                          value={settings.secondary_color}
                          onChange={(e) => setSettings({ ...settings, secondary_color: e.target.value })}
                          className="w-12 h-12 rounded-lg cursor-pointer border-0"
                        />
                        <Input
                          value={settings.secondary_color}
                          onChange={(e) => setSettings({ ...settings, secondary_color: e.target.value })}
                          placeholder="#059669"
                          className="flex-1"
                        />
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Homepage Tab */}
          <TabsContent value="homepage">
            <Card className="bg-white rounded-xl border border-stone-100">
              <CardHeader>
                <CardTitle style={{ fontFamily: 'Outfit' }}>Homepage Content</CardTitle>
                <CardDescription>Customize the landing page hero section</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <Label>Hero Title</Label>
                  <Input
                    value={settings.hero_title}
                    onChange={(e) => setSettings({ ...settings, hero_title: e.target.value })}
                    placeholder="Your Learning Journey Starts Here"
                    className="mt-1"
                    data-testid="hero-title-input"
                  />
                </div>
                <div>
                  <Label>Hero Subtitle</Label>
                  <Textarea
                    value={settings.hero_subtitle}
                    onChange={(e) => setSettings({ ...settings, hero_subtitle: e.target.value })}
                    placeholder="Access lecture recordings, learning materials..."
                    className="mt-1"
                    rows={3}
                    data-testid="hero-subtitle-input"
                  />
                </div>
                <div>
                  <Label>Footer Text</Label>
                  <Input
                    value={settings.footer_text}
                    onChange={(e) => setSettings({ ...settings, footer_text: e.target.value })}
                    placeholder="© 2024 LearnHub. All rights reserved."
                    className="mt-1"
                    data-testid="footer-text-input"
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Contact Tab */}
          <TabsContent value="contact">
            <Card className="bg-white rounded-xl border border-stone-100">
              <CardHeader>
                <CardTitle style={{ fontFamily: 'Outfit' }}>Contact Information</CardTitle>
                <CardDescription>How students can reach you</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <Label className="flex items-center gap-2">
                    <Mail className="h-4 w-4" />
                    Contact Email
                  </Label>
                  <Input
                    type="email"
                    value={settings.contact_email || ""}
                    onChange={(e) => setSettings({ ...settings, contact_email: e.target.value })}
                    placeholder="support@learnhub.com"
                    className="mt-1"
                    data-testid="contact-email-input"
                  />
                </div>
                <div>
                  <Label className="flex items-center gap-2">
                    <Phone className="h-4 w-4" />
                    Contact Phone
                  </Label>
                  <Input
                    value={settings.contact_phone || ""}
                    onChange={(e) => setSettings({ ...settings, contact_phone: e.target.value })}
                    placeholder="+44 123 456 7890"
                    className="mt-1"
                    data-testid="contact-phone-input"
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Social Links Tab */}
          <TabsContent value="social">
            <Card className="bg-white rounded-xl border border-stone-100">
              <CardHeader>
                <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
                  <Globe className="h-5 w-5 text-violet-700" />
                  Social Media Links
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {['facebook', 'twitter', 'instagram', 'linkedin', 'youtube'].map(platform => (
                  <div key={platform}>
                    <Label className="capitalize">{platform}</Label>
                    <Input
                      value={settings.social_links?.[platform] || ""}
                      onChange={(e) => handleSocialLinkChange(platform, e.target.value)}
                      placeholder={`https://${platform}.com/yourpage`}
                      className="mt-1"
                    />
                  </div>
                ))}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default AdminSettings;
